import React, { useRef, useState } from "react";
import { Loader2, Trash2, Upload, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, apiError, mediaUrl } from "../lib/api";

export const PhotoUpload = ({ value, onChange, name = "" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = mediaUrl(value);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/api/uploads/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (error) {
      toast.error(apiError(error, "Could not upload the photo"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary">
        {src ? (
          <img src={src} alt={name || "Profile"} className="h-full w-full object-cover" />
        ) : (
          <User className="h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={handleFile}
          data-testid="photo-upload-input"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="photo-upload-button"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {value ? "Replace photo" : "Upload photo"}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            data-testid="photo-remove-button"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Remove
          </Button>
        ) : null}
        <p className="w-full text-xs text-muted-foreground">PNG, JPG, WEBP or GIF up to 5 MB.</p>
      </div>
    </div>
  );
};
